/**
 * 通用生成 Mixin
 *
 * 封装所有模型页面共用的生成流程、文件上传、积分计算、历史记录等逻辑。
 * 使用方式：在组件中 mixin 后，实现几个扩展点方法即可。
 *
 * 必须提供的 data/methods：
 *   data.mixinModelId        - 模型 ID（如 'wan-2-5', 'kling-2-6'）
 *   data.mixinDefaultAiName  - 默认模型名称（如 'Wan 2.5'）
 *   data.mixinType           - 模型类型 'video' 或 'image'
 *
 * 可选覆写的方法：
 *   validateBeforeGenerate()             - 返回错误消息 string 或 null
 *   resolveAiSource(isOfficial)          - 返回 HISTORY_SOURCE_* 数值
 *   buildPollOptions(response)           - 返回轮询 options 对象
 *   calcResultCredits(statusRes)         - 返回实际消耗积分数
 *   onGenerationComplete(statusRes)      - 完成后的额外处理
 *   onGenerationFailed(statusRes)        - 失败后的额外处理
 */

var apiClient = require('../api/config').default
var generateContent = require('../api/generation').generateContent
var pollGenerationStatus = require('../api/generation').pollGenerationStatus
var uploadFile = require('../api/upload').uploadFile
var saveOrUpdateAIHistory = require('../api/aiHistory').saveOrUpdateAIHistory
var formatCreateDate = require('../api/aiHistory').formatCreateDate
var saveMediaBlob = require('../utils/mediaCache').saveMediaBlob
var saveMediaBlobList = require('../utils/mediaCache').saveMediaBlobList
var proxifyVolcArkTosFetchUrl = require('../api/ai/official/volc-ark-tos-fetch-url').proxifyVolcArkTosFetchUrl
var getOfficialVideoProfile = require('../config/official-channels').getOfficialVideoProfile
var getOfficialImageProfile = require('../config/official-channels').getOfficialImageProfile
var getOfficialMusicProfile = require('../config/official-channels').getOfficialMusicProfile
var HISTORY_SOURCE_POLLO = require('../config/ai-history-source').HISTORY_SOURCE_POLLO
var calcModelCredits = require('../config/modelCredits').calcModelCredits
var getDefaultValues = require('../config/modelParams').getDefaultValues
var buildApiParams = require('../config/modelParams').buildApiParams
var getModelParams = require('../config/modelParams').getModelParams

/* 默认 official 渠道对应的 aiSource 值，按 modelId 前缀映射 */
var OFFICIAL_SOURCE_MAP = {
  'wan-': 104,   /* HISTORY_SOURCE_BAILIAN */
  'kling-': 106, /* HISTORY_SOURCE_KLING */
  'veo-': 103,   /* HISTORY_SOURCE_DASHSCOPE */
  'sora-': 105,  /* HISTORY_SOURCE_SORA */
  'runway': 107, /* HISTORY_SOURCE_RUNWAY */
  'seedance': 104,
  'hailuo-': 108,
  'vidu-': 109,
  'seegen': 104,
  'pollodance': 104
}

function getDefaultOfficialSource(modelId) {
  var keys = Object.keys(OFFICIAL_SOURCE_MAP)
  for (var i = 0; i < keys.length; i++) {
    if (modelId.indexOf(keys[i]) === 0) return OFFICIAL_SOURCE_MAP[keys[i]]
  }
  return HISTORY_SOURCE_POLLO
}

/* ===== 生成任务持久化（刷新恢复轮询） ===== */
var PENDING_KEY = 'pending_generation'
var PENDING_TTL = 10 * 60 * 1000 /* 10 分钟超时 */

function savePendingTask(data, store) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(data))
  } catch (e) { /* ignore */ }
  if (store && store.commit) store.commit('SET_PENDING_GENERATION', data)
}

function loadPendingTask() {
  try {
    var raw = localStorage.getItem(PENDING_KEY)
    if (!raw) return null
    var data = JSON.parse(raw)
    if (!data) { clearPendingTask(); return null }
    /* 过期判定 */
    if (Date.now() - data.createdAt > PENDING_TTL) { return data /* 返回数据用于上报失败 */ }
    return data
  } catch (e) { return null }
}

function clearPendingTask(store) {
  try { localStorage.removeItem(PENDING_KEY) } catch (e) { /* ignore */ }
  if (store && store.commit) store.commit('SET_PENDING_GENERATION', null)
}

/* 根据 official profile 预判初始 aiSource */
function getInitialAiSource(modelId) {
  if (modelId === 'runway') {
    return getDefaultOfficialSource('runway')
  }
  if (modelId === 'hailuo-2-3') {
    return getDefaultOfficialSource('hailuo-2-3')
  }
  if (modelId === 'vidu-q3-pro') {
    return getDefaultOfficialSource('vidu-q3-pro')
  }
  if (modelId.indexOf('kling-') === 0) {
    return getDefaultOfficialSource(modelId)
  }
  if (getOfficialVideoProfile(modelId) || getOfficialImageProfile(modelId) || getOfficialMusicProfile(modelId)) {
    return getDefaultOfficialSource(modelId)
  }
  return HISTORY_SOURCE_POLLO
}

function normalizeHistoryId(id) {
  if (id == null || id === '') return null
  return String(id)
}

function isCustomResolutionAreaValid(size) {
  if (!size || typeof size !== 'object') return false
  var w = parseInt(size.width, 10)
  var h = parseInt(size.height, 10)
  if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return false
  var area = w * h
  var minArea = 512 * 512
  var maxArea = 2048 * 2048
  return area >= minArea && area <= maxArea
}

function isCustomResolutionAreaValidByModel(modelId, size) {
  if (!size || typeof size !== 'object') return false
  var w = parseInt(size.width, 10)
  var h = parseInt(size.height, 10)
  if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return false
  if (modelId === 'sd-3-5' && (w % 64 !== 0 || h % 64 !== 0)) return false
  var area = w * h
  if (modelId === 'seedream-5') {
    return area >= (2560 * 1440) && area <= (4096 * 4096)
  }
  if (modelId === 'seedream-4') {
    return area >= (1280 * 720) && area <= (4096 * 4096)
  }
  return isCustomResolutionAreaValid(size)
}

module.exports = {
  data: function () {
    return {
      userCredits: 0,
      generating: false,
      generated: false,
      generatedVideoUrl: null,
      generatedImageUrl: null,
      generateError: null,
      taskId: null,
      poller: null,
      modelFaqs: [],
      /* 上传状态 */
      uploadLoadingMap: {},
      uploadFileNames: {},
      /* 参数值 */
      paramValues: {}
    }
  },

  computed: {
    /* 当前模型在 store 中的信息 */
    model: function () {
      if (!this.$store || !this.$store.getters || !this.$store.getters.getModelById) {
        return { id: this.mixinModelId, name: this.mixinDefaultAiName, credits: 0, speed: '' }
      }
      return this.$store.getters.getModelById(this.mixinModelId) || {
        id: this.mixinModelId, name: this.mixinDefaultAiName, credits: 0, speed: ''
      }
    },
    /* 所需积分 */
    requiredCredits: function () {
      var config = getModelParams(this.mixinModelId)
      if (!config) return 0
      var result = calcModelCredits({
        modelId: this.mixinModelId,
        baseCredits: (this.model && this.model.credits) || 0,
        durationSec: this.paramValues.videoDuration || this.paramValues.videoLength,
        resolutionKey: this.paramValues.videoRes || this.paramValues.imageSize || this.paramValues.resolution,
        audioOn: this.paramValues.generateAudio,
        genType: this.paramValues.veoGenType || this.paramValues.genType,
        count: this.paramValues.imageCount || 1,
        imageSize: this.paramValues.imageSize,
        modelVersion: this.paramValues.modelVersion,
        pollodanceVariant: this.paramValues.pollodanceVariant
      })
      return (typeof result === 'number' && !isNaN(result)) ? result : ((this.model && this.model.credits) || 0)
    },
    /* 积分是否不足 */
    insufficientCredits: function () {
      return this.userCredits < this.requiredCredits
    }
  },

  created: function () {
    /* 从 schema 初始化参数默认值 */
    var defaults = getDefaultValues(this.mixinModelId)
    this.paramValues = Object.assign({}, defaults)
    /* 从路由 query 恢复首页传入的 prompt 和参数 */
    var query = this.$route && this.$route.query
    if (query) {
      if (query.params) {
        try {
          var passed = JSON.parse(query.params)
          if (passed && typeof passed === 'object') {
            var keys = Object.keys(passed)
            for (var i = 0; i < keys.length; i++) {
              this.$set(this.paramValues, keys[i], passed[keys[i]])
            }
          }
        } catch (e) { /* ignore invalid JSON */ }
      }
      /* prompt 放在 params 之后，确保不被 params 中的空 prompt 覆盖 */
      if (query.prompt) {
        this.paramValues.prompt = query.prompt
      }
    }
  },

  mounted: function () {
    /* 读取用户积分 */
    var u = typeof localStorage !== 'undefined' && localStorage.getItem('userInfo')
    if (u) {
      try {
        var o = JSON.parse(u)
        this.userCredits = o.credits != null ? o.credits : 0
      } catch (e) { /* ignore */ }
    }

    /* 恢复未完成的生成任务 */
    this._recoverPendingTask()

    /* 首页跳转带 __autogenerate 标记时，自动触发生成 */
    if (window.__autogenerate && this.paramValues && this.paramValues.prompt) {
      var self = this
      delete window.__autogenerate
      this.$nextTick(function () {
        self.generate()
      })
    }
  },

  watch: {
    /* 登录/登出时刷新积分和历史记录 */
    '$store.state.userInfo': function (newVal) {
      if (newVal && newVal.credits != null) {
        this.userCredits = newVal.credits
      }
      if (typeof this._fetchHistory === 'function') {
        this._fetchHistory()
      }
    },
    /* 路由切换模型时，重新初始化参数和生成状态 */
    mixinModelId: function (newId) {
      if (!newId) return
      var defaults = getDefaultValues(newId)
      this.paramValues = Object.assign({}, defaults)
      /* 如果 query 中有传入参数，合并到新的默认值上 */
      var query = this.$route && this.$route.query
      if (query && query.params) {
        try {
          var passed = JSON.parse(query.params)
          if (passed && typeof passed === 'object') {
            var keys = Object.keys(passed)
            for (var i = 0; i < keys.length; i++) {
              this.$set(this.paramValues, keys[i], passed[keys[i]])
            }
          }
        } catch (e) { /* ignore */ }
      }
      /* prompt 放在 params 之后，确保不被 params 中的空 prompt 覆盖 */
      if (query && query.prompt) {
        this.paramValues.prompt = query.prompt
      }
      this.generated = false
      this.generatedVideoUrl = null
      this.generatedImageUrl = null
      this.generateError = null
    }
  },

  beforeDestroy: function () {
    /* 清理轮询 */
    if (this.poller && typeof this.poller.stop === 'function') {
      this.poller.stop()
    }
  },

  methods: {
    /* ========================================
     * 恢复未完成的生成任务（页面刷新后）
     * ======================================== */
    _recoverPendingTask: function () {
      var self = this
      var pending = loadPendingTask()
      if (!pending) return

      var isExpired = Date.now() - pending.createdAt > PENDING_TTL

      /* 过期：上报服务器失败，清除记录 */
      if (isExpired) {
        saveOrUpdateAIHistory({
          aiName: pending.aiName || self.mixinDefaultAiName,
          historyId: normalizeHistoryId(pending.historyId),
          taskId: pending.taskId,
          aiDesc: pending.aiDesc || '',
          state: 3,
          message: self.$t('common.genRecoveryTimeout') || 'Generation timed out or page was closed',
          aiResultUrl: '',
          createDate: pending.createDate || formatCreateDate(new Date()),
          aiSource: pending.aiSource || HISTORY_SOURCE_POLLO,
          aiMediaType: pending.type || 'video'
        }).catch(function () {})
        clearPendingTask(self.$store)
        return
      }

      /* 类型不匹配：静默轮询以正确保存历史，不更新当前页面 UI */
      if (pending.type && pending.type !== self.mixinType) {
        self._silentRecoverPending(pending)
        return
      }

      /* 没有 taskId：同步图片请求，静默上报失败 */
      if (!pending.taskId) {
        saveOrUpdateAIHistory({
          aiName: pending.aiName || self.mixinDefaultAiName,
          historyId: normalizeHistoryId(pending.historyId),
          taskId: '',
          aiDesc: pending.aiDesc || '',
          state: 3,
          message: self.$t('common.genRecoveryInterrupted') || 'Generation interrupted by page refresh',
          aiResultUrl: '',
          createDate: pending.createDate || formatCreateDate(new Date()),
          aiSource: pending.aiSource || HISTORY_SOURCE_POLLO,
          aiMediaType: pending.type || 'image'
        }).catch(function () {})
        clearPendingTask(self.$store)
        return
      }

      /* 未过期且有 taskId：恢复轮询 */
      self.generating = true
      self.generated = false
      self.generatedVideoUrl = null
      self.generatedImageUrl = null
      self.generateError = null
      self.taskId = pending.taskId

      /* 通知组件插入 loading 记录 */
      if (typeof self.onGenerationStart === 'function') {
        self.onGenerationStart()
      }

      var aiName = pending.aiName || self.mixinDefaultAiName
      var aiDesc = pending.aiDesc || ''
      var createDate = pending.createDate || formatCreateDate(new Date())
      var historyId = normalizeHistoryId(pending.historyId)
      var aiSource = pending.aiSource || HISTORY_SOURCE_POLLO
      var pollOptions = pending.pollOptions || undefined

      self.poller = pollGenerationStatus(self.taskId, function (err, statusRes) {
        if (err) {
          self.generating = false
          var errMsg = self.$userFacingError
            ? self.$userFacingError(err, 'Generation failed')
            : (err.message || 'Generation failed')
          self.generateError = errMsg
          var msgForHistory = ''
          if (err.error && err.error.message) msgForHistory = err.error.message
          else if (err.message) msgForHistory = err.message
          else if (statusRes && statusRes.error) msgForHistory = statusRes.error
          saveOrUpdateAIHistory({
            aiName: aiName, historyId: historyId, taskId: self.taskId,
            aiDesc: aiDesc, state: 3, message: msgForHistory,
            aiResultUrl: '', createDate: createDate, aiSource: aiSource,
            aiMediaType: pending.type || self.mixinType
          }).catch(function () {})
          clearPendingTask(self.$store)
          return
        }

        if (statusRes.status === 'completed') {
          var creditsUsed = self.calcResultCredits(statusRes)
          var resultUrl = ''
          var urls = statusRes.urls || statusRes.url
          var pendingType = pending.type || self.mixinType

          if (pendingType === 'video') {
            if (Array.isArray(urls) && urls.length > 0) resultUrl = urls[0]
            else if (typeof urls === 'string') resultUrl = urls

            var isOfficial = pollOptions && pollOptions.provider === 'official'
            if (isOfficial && statusRes.videoBlob) {
              var blobSaves = []
              if (historyId) blobSaves.push(saveMediaBlob(historyId, statusRes.videoBlob))
              if (self.taskId) blobSaves.push(saveMediaBlob(self.taskId, statusRes.videoBlob))
              Promise.all(blobSaves).then(function () {
                self.generatedVideoUrl = statusRes.url || resultUrl
                self.generating = false
                self.generated = true
              }).catch(function () {
                self.generatedVideoUrl = statusRes.url || resultUrl
                self.generating = false
                self.generated = true
              })
            } else {
              if (self._shouldCacheWanUnifiedVideoLocally(pollOptions, pending)) {
                self._cacheVideoByUrl(historyId, self.taskId, resultUrl)
              }
              self.generatedVideoUrl = resultUrl
              self.generating = false
              self.generated = true
            }
          } else {
            if (Array.isArray(urls) && urls.length > 0) resultUrl = urls[0]
            else if (typeof urls === 'string') resultUrl = urls
            self.generatedImageUrl = resultUrl
            self.generating = false
            self.generated = true
            self._cacheWanUnifiedImageBlobsIfNeeded(pollOptions, historyId, statusRes, resultUrl, pending)
          }

          self.updateLocalCredits(statusRes)
          var saveEmptyResultUrl = self._shouldSaveEmptyResultUrl(pollOptions, resultUrl, pending)
          saveOrUpdateAIHistory({
            aiName: aiName, historyId: historyId, taskId: self.taskId,
            aiDesc: aiDesc, state: 2, aiResultUrl: saveEmptyResultUrl ? '' : resultUrl,
            createDate: createDate, credits: creditsUsed, aiSource: aiSource,
            aiMediaType: pending.type || self.mixinType
          }).catch(function () {})

          if (typeof self.onGenerationComplete === 'function') {
            self.onGenerationComplete(statusRes)
          }
          clearPendingTask(self.$store)
        } else if (statusRes.status === 'failed') {
          self.generating = false
          self.generateError = statusRes.message || statusRes.error || 'Generation failed'
          saveOrUpdateAIHistory({
            aiName: aiName, historyId: historyId, taskId: self.taskId,
            aiDesc: aiDesc, state: 3,
            message: statusRes.message || statusRes.error || '',
            aiResultUrl: '', createDate: createDate, aiSource: aiSource,
            aiMediaType: pending.type || self.mixinType
          }).catch(function () {})
          if (typeof self.onGenerationFailed === 'function') {
            self.onGenerationFailed(statusRes)
          }
          clearPendingTask(self.$store)
        }
      }, self._getPollIntervalByOptions(pollOptions), pollOptions)
    },

    /**
     * 静默恢复不匹配类型的 pending task
     * 只轮询 + 保存历史 + 保存 blob，不修改当前页面 UI 状态
     */
    _silentRecoverPending: function (pending) {
      var self = this
      if (!pending || !pending.taskId) return

      var aiName = pending.aiName || self.mixinDefaultAiName
      var aiDesc = pending.aiDesc || ''
      var createDate = pending.createDate || formatCreateDate(new Date())
      var historyId = normalizeHistoryId(pending.historyId)
      var aiSource = pending.aiSource || HISTORY_SOURCE_POLLO
      var pollOptions = pending.pollOptions || undefined
      var pendingType = pending.type || 'video'

      self.poller = pollGenerationStatus(pending.taskId, function (err, statusRes) {
        if (err) {
          var msgForHistory = ''
          if (err.error && err.error.message) msgForHistory = err.error.message
          else if (err.message) msgForHistory = err.message
          else if (statusRes && statusRes.error) msgForHistory = statusRes.error
          saveOrUpdateAIHistory({
            aiName: aiName, historyId: historyId, taskId: pending.taskId,
            aiDesc: aiDesc, state: 3, message: msgForHistory,
            aiResultUrl: '', createDate: createDate, aiSource: aiSource,
            aiMediaType: pendingType
          }).catch(function () {})
          clearPendingTask(self.$store)
          return
        }

        if (statusRes.status === 'completed') {
          var urls = statusRes.urls || statusRes.url
          var resultUrl = ''
          if (Array.isArray(urls) && urls.length > 0) resultUrl = urls[0]
          else if (typeof urls === 'string') resultUrl = urls

          /* 视频类型：official 渠道需要保存 blob 到 IndexedDB */
          if (pendingType === 'video') {
            var isOfficial = pollOptions && pollOptions.provider === 'official'
            if (isOfficial && statusRes.videoBlob) {
              var blobSaves = []
              if (historyId) blobSaves.push(saveMediaBlob(historyId, statusRes.videoBlob))
              if (pending.taskId) blobSaves.push(saveMediaBlob(pending.taskId, statusRes.videoBlob))
              Promise.all(blobSaves).catch(function () {})
            } else if (self._shouldCacheWanUnifiedVideoLocally(pollOptions, pending)) {
              self._cacheVideoByUrl(historyId, pending.taskId, resultUrl)
            }
          } else if (pendingType === 'image') {
            self._cacheWanUnifiedImageBlobsIfNeeded(pollOptions, historyId, statusRes, resultUrl, pending)
          }

          /* 保存历史记录（使用 pending.type 作为 aiMediaType） */
          var saveEmptyResultUrl = self._shouldSaveEmptyResultUrl(pollOptions, resultUrl, pending)
          saveOrUpdateAIHistory({
            aiName: aiName, historyId: historyId, taskId: pending.taskId,
            aiDesc: aiDesc, state: 2, aiResultUrl: saveEmptyResultUrl ? '' : resultUrl,
            createDate: createDate, aiSource: aiSource,
            aiMediaType: pendingType
          }).catch(function () {})
          clearPendingTask(self.$store)
        } else if (statusRes.status === 'failed') {
          saveOrUpdateAIHistory({
            aiName: aiName, historyId: historyId, taskId: pending.taskId,
            aiDesc: aiDesc, state: 3,
            message: statusRes.message || statusRes.error || '',
            aiResultUrl: '', createDate: createDate, aiSource: aiSource,
            aiMediaType: pendingType
          }).catch(function () {})
          clearPendingTask(self.$store)
        }
      }, self._getPollIntervalByOptions(pollOptions), pollOptions)
    },

    /**
     * 轮询返回的单张图：HTTPS 走代理 fetch；data URL / 纯 base64（如 gpt-image）本地解码为 Blob，不落远端 URL
     */
    _blobFromImageResultString: function (u) {
      var self = this
      var s = String(u || '').trim()
      if (!s) return Promise.resolve(null)
      if (s.indexOf('data:') === 0) {
        return fetch(s).then(function (r) {
          return r && r.ok ? r.blob() : null
        }).catch(function () { return null })
      }
      /* poll 侧已对 TOS 等做过 proxify 时，此处常为同源代理路径 /xxxProxy/...，须按路径 fetch（仅匹配 https 会误走 base64 分支） */
      var normalized = s
      if (/^\/\//.test(s)) {
        var proto = 'https:'
        try {
          if (typeof window !== 'undefined' && window.location && window.location.protocol) {
            proto = window.location.protocol
          }
        } catch (eProto) {}
        normalized = proto + s
      }
      var fu = proxifyVolcArkTosFetchUrl(normalized)
      var httpLike = /^https?:\/\//i.test(fu)
      var sameOriginPath = fu.charAt(0) === '/' && fu.charAt(1) !== '/'
      if (sameOriginPath) {
        return fetch(fu, { mode: 'cors', credentials: 'omit' }).then(function (r) {
          return r && r.ok ? r.blob() : null
        }).catch(function () { return null })
      }
      if (httpLike) {
        return self._fetchBlobViaDownload(fu)
      }
      try {
        var b64 = s.replace(/\s/g, '')
        var bin = atob(b64)
        var bytes = new Uint8Array(bin.length)
        for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        return Promise.resolve(new Blob([bytes], { type: 'image/png' }))
      } catch (e) {
        return Promise.resolve(null)
      }
    },

    /** wan-unified 图片：与原先同步路径一致，fetch 落 IndexedDB，便于 aiResultUrl 为空时侧栏展示 */
    _cacheWanUnifiedImageBlobsIfNeeded: function (pollOptions, historyId, statusRes, resultUrl, pending) {
      var self = this
      if (!pollOptions || pollOptions.provider !== 'wan-unified') return
      if (!historyId) return
      if (!self._shouldSaveEmptyResultUrl(pollOptions, resultUrl, pending || {})) return
      var urlList = []
      if (statusRes && Array.isArray(statusRes.urls) && statusRes.urls.length) urlList = statusRes.urls
      else if (resultUrl) urlList = [resultUrl]
      if (!urlList.length) return
      var fetchTasks = urlList.map(function (u) {
        return self._blobFromImageResultString(u)
      })
      Promise.all(fetchTasks).then(function (blobs) {
        var valid = blobs.filter(Boolean)
        if (!valid.length) return
        if (valid.length > 1) return saveMediaBlobList(historyId, valid).catch(function () {})
        return saveMediaBlob(historyId, valid[0]).catch(function () {})
      }).then(function () {
        if (typeof self._loadLocalMediaCache === 'function' && self.historyItems && self.historyItems.length) {
          self._loadLocalMediaCache(self.historyItems)
        }
      }).catch(function () {})
    },

    /* ========================================
     * 参数变更处理（供 ModelParams 组件调用）
     * ======================================== */
    onParamChange: function (key, value) {
      this.$set(this.paramValues, key, value)
    },

    /* 文件上传处理 */
    onParamUpload: function (payload) {
      var self = this
      var key = payload.paramKey
      var file = payload.file
      var param = payload.param

      self.$set(self.uploadLoadingMap, key, true)
      self.generateError = null

      /* 多图 URL 上传（如 pollodance refs） */
      if (param.props && param.props.multiUrl) {
        uploadFile(file).then(function (url) {
          var current = self.paramValues[key] || []
          if (!Array.isArray(current)) current = []
          if (param.props.maxCount && current.length >= param.props.maxCount) {
            self.$set(self.uploadLoadingMap, key, false)
            return
          }
          current.push(url)
          self.$set(self.paramValues, key, current)
          self.$set(self.uploadLoadingMap, key, false)
        }).catch(function (err) {
          self.generateError = self.$userFacingError
            ? self.$userFacingError(err, 'Upload failed')
            : (err.message || 'Upload failed')
          self.$set(self.uploadLoadingMap, key, false)
        })
        return
      }

      /* 多图 base64 上传（如 midjourney） */
      if (param.props && param.props.base64) {
        var reader = new FileReader()
        reader.onload = function (e) {
          var base64 = e.target.result
          var current = self.paramValues[key] || []
          if (!Array.isArray(current)) current = []
          if (param.props.maxCount && current.length >= param.props.maxCount) {
            self.$set(self.uploadLoadingMap, key, false)
            return
          }
          current.push(base64)
          self.$set(self.paramValues, key, current)
          self.$set(self.uploadLoadingMap, key, false)
        }
        reader.readAsDataURL(file)
        return
      }

      /* 普通文件上传（获取 URL） */
      uploadFile(file).then(function (url) {
        self.$set(self.paramValues, key, url)
        if (file.name) {
          self.$set(self.uploadFileNames, key, file.name)
        }
      }).catch(function (err) {
        self.generateError = self.$userFacingError
          ? self.$userFacingError(err, 'Upload failed')
          : (err.message || 'Upload failed')
      }).then(function () {
        self.$set(self.uploadLoadingMap, key, false)
      })
    },

    /* 移除已上传文件 */
    onRemoveUpload: function (key) {
      this.$set(this.paramValues, key, null)
      this.$set(this.uploadFileNames, key, '')
    },

    /* ========================================
     * 生成流程
     * ======================================== */
    generate: function () {
      var self = this

      /* 防重入 */
      if (self.generating) return

      /* 前置校验 */
      var validateError = self.validateBeforeGenerate()
      if (validateError) {
        self.generateError = validateError
        return
      }

      self.generating = true
      self.generated = false
      self.generatedVideoUrl = null
      self.generatedImageUrl = null
      self.generateError = null

      /* 通知组件生已开始（同步，用于插入 loading 记录等） */
      if (typeof self.onGenerationStart === 'function') {
        self.onGenerationStart()
      }

      /* 组装参数 */
      var params = buildApiParams(self.mixinModelId, self.paramValues)
      var aiName = (self.model && self.model.name) ? self.model.name : self.mixinDefaultAiName
      var aiDesc = (self.paramValues.prompt || '').trim()
      var config = getModelParams(self.mixinModelId)
      if (config && config.promptMaxlength === 0) {
        aiDesc = ''
      } else if (config && config.promptMaxlength) {
        aiDesc = aiDesc.slice(0, config.promptMaxlength)
      }
      params.prompt = aiDesc

      /* 确定 aiSource（根据 official profile 预判） */
      var aiSource = getInitialAiSource(self.mixinModelId)
      var createDate = formatCreateDate(new Date())

      /* 保存初始历史记录 */
      var historyId = null
      var startHistoryPromise = saveOrUpdateAIHistory({
        aiName: aiName,
        taskId: '',
        aiDesc: aiDesc,
        state: 1,
        aiResultUrl: '',
        createDate: createDate,
        aiSource: aiSource,
        aiMediaType: self.mixinType
      }).then(function (hRes) {
        historyId = normalizeHistoryId(hRes && hRes.historyId)
        /* 请求前就存 pending（覆盖同步图片刷新场景） */
        savePendingTask({
          taskId: '',
          modelId: self.mixinModelId,
          type: self.mixinType,
          aiName: aiName,
          aiDesc: aiDesc,
          aiSource: aiSource,
          historyId: historyId,
          createDate: createDate,
          pollOptions: null,
          createdAt: Date.now()
        }, self.$store)
      }).catch(function () {})

      /* 提交生成任务 */
      startHistoryPromise.then(function () {
        return generateContent(params)
      }).then(function (response) {
        self.taskId = response.taskId || response.task_id

        /* 确定 provider */
        var isOfficial = response.provider === 'official'
        var isAllall = response.provider === 'allall'

        /* 更新 aiSource */
        aiSource = self.resolveAiSource(isOfficial, response)

        /* 更新历史记录（补充 taskId） */
        if (self.taskId) {
          saveOrUpdateAIHistory({
            aiName: aiName,
            historyId: historyId,
            taskId: self.taskId,
            aiDesc: aiDesc,
            state: 1,
            aiResultUrl: '',
            createDate: createDate,
            aiSource: aiSource,
            aiMediaType: self.mixinType
          }).then(function (hRes) {
            historyId = normalizeHistoryId(hRes && hRes.historyId)
          }).catch(function () {})
        }

        /* ========== 同步响应（allall 图片、official 图片等） ========== */
        if (response.sync) {
          self._handleSyncResponse(response, {
            historyId: historyId, aiName: aiName, aiDesc: aiDesc,
            createDate: createDate, aiSource: aiSource,
            isAllall: isAllall, isOfficial: isOfficial
          })
          clearPendingTask(self.$store)
          return
        }

        /* ========== 异步响应 — 轮询 ========== */
        if (!self.taskId) {
          self.generating = false
          self.generateError = 'No task ID returned'
          clearPendingTask(self.$store)
          return
        }

        /* 构建轮询选项 */
        var pollOptions = self.buildPollOptions(response)

        /* 持久化 pending 任务（刷新恢复用） */
        savePendingTask({
          taskId: self.taskId,
          modelId: self.mixinModelId,
          type: self.mixinType,
          aiName: aiName,
          aiDesc: aiDesc,
          aiSource: aiSource,
          historyId: historyId,
          createDate: createDate,
          pollOptions: pollOptions,
          createdAt: Date.now()
        }, self.$store)

        /* 通知组件任务已提交（taskId + 轮询参数，用于持久化恢复） */
        if (typeof self.onTaskSubmitted === 'function') {
          self.onTaskSubmitted(self.taskId, pollOptions)
        }

        /* 开始轮询 */
        self.poller = pollGenerationStatus(self.taskId, function (err, statusRes) {
          if (err) {
            self.generating = false
            var errMsg = self.$userFacingError
              ? self.$userFacingError(err, 'Generation failed')
              : (err.message || 'Generation failed')
            self.generateError = errMsg
            var msgForHistory = ''
            if (err.error && err.error.message) msgForHistory = err.error.message
            else if (err.message) msgForHistory = err.message
            else if (statusRes && statusRes.error) msgForHistory = statusRes.error
            saveOrUpdateAIHistory({
              aiName: aiName,
              historyId: historyId,
              taskId: self.taskId,
              aiDesc: aiDesc,
              state: 3,
              message: msgForHistory,
              aiResultUrl: '',
              createDate: createDate,
              aiSource: aiSource,
              aiMediaType: self.mixinType
            }).catch(function () {})
            clearPendingTask(self.$store)
            return
          }

          if (statusRes.status === 'completed') {
            /* 计算实际消耗积分 */
            var creditsUsed = self.calcResultCredits(statusRes)
            var resultUrl = ''
            var urls = statusRes.urls || statusRes.url

            /* 处理结果 URL */
            if (self.mixinType === 'video') {
              if (Array.isArray(urls) && urls.length > 0) {
                resultUrl = urls[0]
              } else if (typeof urls === 'string') {
                resultUrl = urls
              }
              /* official channel — 保存 videoBlob */
              if (isOfficial && statusRes.videoBlob) {
                var blobSaves = []
                if (historyId) blobSaves.push(saveMediaBlob(historyId, statusRes.videoBlob))
                if (self.taskId) blobSaves.push(saveMediaBlob(self.taskId, statusRes.videoBlob))
                Promise.all(blobSaves).then(function () {
                  self.generatedVideoUrl = statusRes.url || resultUrl
                  self.generating = false
                  self.generated = true
                }).catch(function () {
                  self.generatedVideoUrl = statusRes.url || resultUrl
                  self.generating = false
                  self.generated = true
                })
              } else {
                if (self._shouldCacheWanUnifiedVideoLocally(pollOptions, { modelId: self.mixinModelId })) {
                  self._cacheVideoByUrl(historyId, self.taskId, resultUrl)
                }
                self.generatedVideoUrl = resultUrl
                self.generating = false
                self.generated = true
              }
            } else {
              /* image */
              if (Array.isArray(urls) && urls.length > 0) {
                resultUrl = urls[0]
              } else if (typeof urls === 'string') {
                resultUrl = urls
              }
              self.generatedImageUrl = resultUrl
              self.generating = false
              self.generated = true
              self._cacheWanUnifiedImageBlobsIfNeeded(pollOptions, historyId, statusRes, resultUrl, { modelId: self.mixinModelId })
            }

            /* 更新积分 */
            self.updateLocalCredits(statusRes)

            /* 保存历史记录（成功）— official 渠道存空 URL 避免过期 */
            saveOrUpdateAIHistory({
              aiName: aiName,
              historyId: historyId,
              taskId: self.taskId,
              aiDesc: aiDesc,
              state: 2,
              aiResultUrl: self._shouldSaveEmptyResultUrl(pollOptions, resultUrl, { modelId: self.mixinModelId }) ? '' : resultUrl,
              createDate: createDate,
              credits: creditsUsed,
              aiSource: aiSource,
              aiMediaType: self.mixinType
            }).catch(function () {})

            /* 完成回调 */
            if (typeof self.onGenerationComplete === 'function') {
              self.onGenerationComplete(statusRes)
            }
            clearPendingTask(self.$store)
          } else if (statusRes.status === 'failed') {
            self.generating = false
            self.generateError = statusRes.message || statusRes.error || 'Generation failed'
            saveOrUpdateAIHistory({
              aiName: aiName,
              historyId: historyId,
              taskId: self.taskId,
              aiDesc: aiDesc,
              state: 3,
              message: statusRes.message || statusRes.error || '',
              aiResultUrl: '',
              createDate: createDate,
              aiSource: aiSource,
              aiMediaType: self.mixinType
            }).catch(function () {})

            if (typeof self.onGenerationFailed === 'function') {
              self.onGenerationFailed(statusRes)
            }
            clearPendingTask(self.$store)
          }
          /* status === 'processing' 时继续轮询 */
        }, self._getPollIntervalByOptions(pollOptions), pollOptions)
      }).catch(function (error) {
        self.generating = false
        self.generateError = self.$userFacingError
          ? self.$userFacingError(error, 'Generation failed')
          : (error.message || 'Generation failed')
        saveOrUpdateAIHistory({
          aiName: (self.model && self.model.name) || self.mixinDefaultAiName,
          historyId: historyId,
          taskId: self.taskId || '',
          aiDesc: (self.paramValues.prompt || '').trim(),
          state: 3,
          message: error.message || '',
          aiResultUrl: '',
          createDate: createDate,
          aiSource: aiSource,
          aiMediaType: self.mixinType
        }).catch(function () {})
        clearPendingTask(self.$store)
      })
    },

    /* ========================================
     * 同步响应处理（内部方法）
     * ======================================== */

    /**
     * 处理同步响应 — allall 图片 blob 下载、official 图片 blob、通用直接 URL
     */
    _handleSyncResponse: function (response, ctx) {
      var self = this
      var urls = (response.urls && Array.isArray(response.urls)) ? response.urls : []
      var shouldStoreEmptyResultUrl = ctx.isOfficial
      function cacheFromUrls() {
        if (!shouldStoreEmptyResultUrl || !ctx.historyId || !urls.length) return
        var downloadPromises = urls.map(function (u) {
          var fetchUrl = proxifyVolcArkTosFetchUrl(String(u))
          return fetch(fetchUrl, { mode: 'cors', credentials: 'omit' }).then(function (r) {
            if (!r || !r.ok) return null
            return r.blob()
          }).catch(function () { return null })
        })
        Promise.all(downloadPromises).then(function (blobs) {
          var valid = blobs.filter(Boolean)
          if (!valid.length) return
          if (valid.length > 1) return saveMediaBlobList(ctx.historyId, valid)
          return saveMediaBlob(ctx.historyId, valid[0])
        }).catch(function () {})
      }

      if (urls.length === 0) {
        self.generating = false
        self.generateError = 'No results returned'
        saveOrUpdateAIHistory({
          aiName: ctx.aiName, historyId: ctx.historyId, taskId: '',
          aiDesc: ctx.aiDesc, state: 3, message: 'No results returned',
          aiResultUrl: '', createDate: ctx.createDate, aiSource: ctx.aiSource,
          aiMediaType: self.mixinType
        }).catch(function () {})
        return
      }

      /* AllAll 渠道 — 通过代理下载 blob 并缓存 */
      if (ctx.isAllall && ctx.historyId) {
        var downloadPromises = urls.map(function (u) {
          var fetchUrl = proxifyVolcArkTosFetchUrl(String(u))
          return fetch(fetchUrl, { mode: 'cors', credentials: 'omit' }).then(function (r) {
            return r.blob()
          })
        })
        Promise.all(downloadPromises).then(function (blobs) {
          /* 缓存 blob */
          var cachePromise = blobs.length > 1
            ? saveMediaBlobList(ctx.historyId, blobs)
            : saveMediaBlob(ctx.historyId, blobs[0])
          return cachePromise.then(function () {
            return blobs.map(function (b) { return URL.createObjectURL(b) })
          })
        }).then(function (objectUrls) {
          self.generatedImageUrl = objectUrls[0]
          self.generating = false
          self.generated = true
          var creditsUsed = self.requiredCredits * (objectUrls.length || 1)
          self.updateLocalCredits(response)
          saveOrUpdateAIHistory({
            aiName: ctx.aiName, historyId: ctx.historyId, taskId: '',
            aiDesc: ctx.aiDesc, state: 2, aiResultUrl: urls[0] || '',
            createDate: ctx.createDate, credits: creditsUsed, aiSource: ctx.aiSource,
            aiMediaType: self.mixinType
          }).catch(function () {})
          if (typeof self.onGenerationComplete === 'function') {
            self.onGenerationComplete({ urls: objectUrls, status: 'completed' })
          }
        }).catch(function () {
          /* blob 下载失败，直接使用原始 URL */
          self.generatedImageUrl = urls[0]
          self.generating = false
          self.generated = true
          var creditsUsed = self.requiredCredits * (urls.length || 1)
          saveOrUpdateAIHistory({
            aiName: ctx.aiName, historyId: ctx.historyId, taskId: '',
            aiDesc: ctx.aiDesc, state: 2, aiResultUrl: urls[0] || '',
            createDate: ctx.createDate, credits: creditsUsed, aiSource: ctx.aiSource,
            aiMediaType: self.mixinType
          }).catch(function () {})
          if (typeof self.onGenerationComplete === 'function') {
            self.onGenerationComplete({ urls: urls, status: 'completed' })
          }
        })
        return
      }

      /* Official 渠道同步（图片）— blobs 可能已在 response 中 */
      if (shouldStoreEmptyResultUrl && response.blobs && response.blobs.length > 0) {
        var objectUrls = response.blobs.map(function (b) { return URL.createObjectURL(b) })
        /* 缓存 blob */
        if (response.blobs.length > 1) {
          saveMediaBlobList(ctx.historyId, response.blobs).catch(function () {})
        } else {
          saveMediaBlob(ctx.historyId, response.blobs[0]).catch(function () {})
        }
        self.generatedImageUrl = objectUrls[0]
        self.generating = false
        self.generated = true
        var creditsUsed = self.requiredCredits * (objectUrls.length || 1)
        self.updateLocalCredits(response)
        /* official 渠道存空 URL 避免过期 */
        saveOrUpdateAIHistory({
          aiName: ctx.aiName, historyId: ctx.historyId, taskId: '',
          aiDesc: ctx.aiDesc, state: 2, aiResultUrl: '',
          createDate: ctx.createDate, credits: creditsUsed, aiSource: ctx.aiSource,
          aiMediaType: self.mixinType
        }).catch(function () {})
        if (typeof self.onGenerationComplete === 'function') {
          self.onGenerationComplete({ urls: objectUrls, status: 'completed' })
        }
        return
      }

      /* 通用同步 — 直接使用 URL */
      self.generatedImageUrl = urls[0]
      self.generating = false
      self.generated = true
      cacheFromUrls()
      var creditsUsed = self.requiredCredits * (urls.length || 1)
      self.updateLocalCredits(response)
      saveOrUpdateAIHistory({
        aiName: ctx.aiName, historyId: ctx.historyId, taskId: '',
        aiDesc: ctx.aiDesc, state: 2, aiResultUrl: shouldStoreEmptyResultUrl ? '' : (urls[0] || ''),
        createDate: ctx.createDate, credits: creditsUsed, aiSource: ctx.aiSource,
        aiMediaType: self.mixinType
      }).catch(function () {})
      if (typeof self.onGenerationComplete === 'function') {
        self.onGenerationComplete({ urls: urls, status: 'completed' })
      }
    },

    /* ========================================
     * 扩展点方法（组件可覆写）
     * ======================================== */

    /**
     * 生成前校验，返回错误消息 string 或 null
     */
    validateBeforeGenerate: function () {
      if (this.insufficientCredits) {
        return 'Insufficient credits'
      }
      if (this.mixinModelId === 'wan-animate') {
        if (!this.paramValues.uploadedImage) return this.$t('modelIntro.wanAnimate.uploadImage')
        if (!this.paramValues.uploadedVideo) return this.$t('modelIntro.wanAnimate.uploadVideoRequired')
      }
      if (this.mixinModelId === 'seegen' && this.paramValues.videoMode === 'image' && !this.paramValues.uploadedImage) {
        return this.$t('modelIntro.seedance.imageRequired')
      }
      if (this.mixinModelId === 'pollodance') {
        var pv = this.paramValues.pollodanceVariant || '2-0'
        if ((pv === '2-0' || pv === '2-0-fast') && this.paramValues.videoMode === 'image' && !this.paramValues.uploadedImage) {
          return this.$t('modelIntro.seedance.imageRequired')
        }
      }
      if ((this.mixinModelId === 'z-image-turbo' || this.mixinModelId === 'qwen-image-2-0-pro' || this.mixinModelId === 'flux-2' || this.mixinModelId === 'sd-3-5') && this.paramValues.resolutionPreset === 'custom') {
        if (!isCustomResolutionAreaValidByModel(this.mixinModelId, this.paramValues.customSize)) {
          if (this.mixinModelId === 'sd-3-5') {
            return 'Custom resolution area must be between 512*512 and 2048*2048, and width/height must be multiples of 64'
          }
          return 'Custom resolution area must be between 512*512 and 2048*2048'
        }
      }
      if ((this.mixinModelId === 'seedream-4' || this.mixinModelId === 'seedream-5') && this.paramValues.sizeMode === 'custom' && this.paramValues.customPreset === 'custom') {
        if (!isCustomResolutionAreaValidByModel(this.mixinModelId, this.paramValues.customSize)) {
          if (this.mixinModelId === 'seedream-5') {
            return 'Custom resolution area must be between 2560*1440 and 4096*4096'
          }
          return 'Custom resolution area must be between 1280*720 and 4096*4096'
        }
      }
      var config = getModelParams(this.mixinModelId)
      var shouldRequirePrompt = !(config && config.promptMaxlength === 0)
      if (shouldRequirePrompt && (!this.paramValues.prompt || !this.paramValues.prompt.trim())) {
        return 'Please enter a prompt'
      }
      return null
    },

    /**
     * 根据 official/pollo 渠道确定 aiSource
     * @param {boolean} isOfficial - 是否官方渠道
     * @param {object} response - generateContent 的返回值
     * @returns {number} HISTORY_SOURCE_* 常量
     */
    resolveAiSource: function (isOfficial, response) {
      if (isOfficial) {
        return getDefaultOfficialSource(this.mixinModelId)
      }
      if (response && response.provider === 'wan-unified') {
        if (this.mixinModelId === 'runway') return getDefaultOfficialSource('runway')
        if (this.mixinModelId === 'hailuo-2-3') return getDefaultOfficialSource('hailuo-2-3')
        if (this.mixinModelId === 'vidu-q3-pro') return getDefaultOfficialSource('vidu-q3-pro')
        if (this.mixinModelId.indexOf('kling-') === 0) return getDefaultOfficialSource(this.mixinModelId)
        if (this.mixinModelId.indexOf('veo-') === 0) return getDefaultOfficialSource(this.mixinModelId)
      }
      return HISTORY_SOURCE_POLLO
    },

    /**
     * 构建轮询选项
     * @param {object} response - generateContent 的返回值
     * @returns {object|undefined} pollOptions
     */
    buildPollOptions: function (response) {
      if (response.provider) {
        return {
          provider: response.provider,
          officialProfile: response.officialProfile,
          officialMedia: response.officialMedia,
          allallApi: response.allallApi,
          wanCreateId: response.createId,
          wanModelName: response.wanModelName,
          wanModelVersion: response.wanModelVersion,
          wanModelType: response.wanModelType,
          wanPollingUrl: response.wanPollingUrl
        }
      }
      return undefined
    },

    _getPollIntervalByOptions: function (options) {
      var provider = options && options.provider
      if (provider === 'wan-unified') return 15000
      return 3000
    },

    /** wan-unified 完成后均尝试拉 mp4 写 IndexedDB（videocdn.pollo.ai 仍跳过缓存，见 _shouldUseDirectResultUrl） */
    _shouldCacheWanUnifiedVideoLocally: function (pollOptions, pending) {
      if (!pollOptions || pollOptions.provider !== 'wan-unified') return false
      return true
    },

    _shouldSaveEmptyResultUrl: function (options, resultUrl, pending) {
      var provider = options && options.provider
      /* official：videoBlob 落 IndexedDB，接口存空链 */
      if (provider === 'official') return true
      /* wan-unified：会走本地缓存的不传远端链；Pollo videocdn 跳过缓存须上报 URL */
      if (provider === 'wan-unified') {
        var mid = pending && pending.modelId
        /* gpt-image：轮询结果为 base64，仅存 IndexedDB，历史上报空 aiResultUrl */
        if (mid === 'gpt-image-1' || mid === 'gpt-image-1-5') return true
        if (this._resultUrlIsInlineImageData(resultUrl)) return true
        if (this._shouldUseDirectResultUrl(resultUrl)) return false
        /* Flux：BFL 作品链（*.bfl.ai）也走本地缓存 */
        return true
      }
      return false
    },

    /** https(s) 作品 URL 的 host 含 bfl.ai（如 api.us3 / delivery.eu2） */
    _resultUrlHostContainsBflAi: function (resultUrl) {
      var s = String(resultUrl || '').trim()
      if (!s) return false
      try {
        var u = new URL(s)
        return String(u.hostname || '').indexOf('bfl.ai') !== -1
      } catch (e) {
        return false
      }
    },

    /** data URL 或纯 base64 串（非 http） */
    _resultUrlIsInlineImageData: function (resultUrl) {
      var s = String(resultUrl || '').trim()
      if (!s) return false
      if (s.indexOf('data:') === 0) return true
      if (/^https?:\/\//i.test(s)) return false
      if (s.length < 32) return false
      return /^[A-Za-z0-9+/=\s]+$/.test(s)
    },

    _shouldUseDirectResultUrl: function (resultUrl) {
      var s = String(resultUrl || '').trim()
      if (!s) return false
      try {
        return new URL(s).host === 'videocdn.pollo.ai'
      } catch (e) {
        return false
      }
    },

    /** 将结果字符串（URL / data: URL / 纯 base64）解析为视频 Blob，复用图片逻辑的判断分支 */
    _blobFromVideoResultString: function (u) {
      var self = this
      var s = String(u || '').trim()
      if (!s) return Promise.resolve(null)
      /* data:video/mp4;base64,... 浏览器可直接 fetch 解码 */
      if (s.indexOf('data:') === 0) {
        return fetch(s).then(function (r) {
          return r && r.ok ? r.blob() : null
        }).catch(function () { return null })
      }
      /* 同源代理路径，直接 fetch */
      var sameOriginPath = s.charAt(0) === '/' && s.charAt(1) !== '/'
      if (sameOriginPath) {
        return fetch(s, { mode: 'cors', credentials: 'omit' }).then(function (r) {
          return r && r.ok ? r.blob() : null
        }).catch(function () { return null })
      }
      /* HTTP/HTTPS 走后端代理下载 */
      var httpLike = /^https?:\/\//i.test(s)
      if (httpLike) {
        return self._fetchBlobViaDownload(s)
      }
      /* 纯 base64 字符串，本地解码 */
      try {
        var b64 = s.replace(/\s/g, '')
        var bin = atob(b64)
        var bytes = new Uint8Array(bin.length)
        for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        return Promise.resolve(new Blob([bytes], { type: 'video/mp4' }))
      } catch (e) {
        return Promise.resolve(null)
      }
    },

    _cacheVideoByUrl: function (historyId, taskId, resultUrl) {
      var self = this
      var url = String(resultUrl || '').trim()
      if (!url) return
      if (this._shouldUseDirectResultUrl(url)) return
      self._blobFromVideoResultString(url).then(function (blob) {
        if (!blob) return
        var tasks = []
        if (historyId) tasks.push(saveMediaBlob(historyId, blob))
        if (taskId) tasks.push(saveMediaBlob(taskId, blob))
        if (!tasks.length) return
        return Promise.all(tasks)
      }).then(function () {
        if (typeof self._loadLocalMediaCache === 'function' && self.historyItems && self.historyItems.length) {
          self._loadLocalMediaCache(self.historyItems)
        }
      }).catch(function () {})
    },

    /**
     * 计算实际消耗积分
     * @param {object} statusRes - 轮询完成时的状态结果
     * @returns {number} 积分数
     */
    calcResultCredits: function (statusRes) {
      var finalDeduction = Number(statusRes.finalUnitDeduction)
      if (!isNaN(finalDeduction) && finalDeduction > 0) {
        return finalDeduction
      }
      var urls = statusRes.urls || statusRes.url
      var count = Array.isArray(urls) ? urls.length : 1
      return this.requiredCredits * count
    },

    /**
     * 更新本地积分缓存
     * @param {object} statusRes - 轮询完成时的状态结果
     */
    updateLocalCredits: function (statusRes) {
      if (typeof statusRes.totalCredits === 'number') {
        this.userCredits = statusRes.totalCredits
        var u = typeof localStorage !== 'undefined' && localStorage.getItem('userInfo')
        if (u) {
          try {
            var o = JSON.parse(u)
            o.credits = statusRes.totalCredits
            localStorage.setItem('userInfo', JSON.stringify(o))
          } catch (e) { /* ignore */ }
        }
      }
    },

    /** 通过后端 POST /download 接口获取 blob，避免跨域 */
    _fetchBlobViaDownload: function (url) {
      return apiClient.post('/download', { url: url }, {
        responseType: 'blob',
        timeout: 120000,
        transformResponse: [function (data) { return data }]
      }).then(function (res) {
        if (res && res.size && res.size > 0) return res
        return null
      }).catch(function () {
        return null
      })
    }
  }
}
